import { useRef, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
// Relative import (not the '@/components' barrel) to avoid a cycle: the barrel
// exports Workbench, which renders the toolbars that use these bits.
import { Popover, PopoverAnchor, PopoverContent } from '../Popover/Popover'

// Small presentational bits + constants shared by the workbench floating/style
// toolbars. Split out to keep the toolbar files small.

export const WIDTHS = [1, 2, 3, 4]
export const STYLES = [
  { id: 'solid', label: 'Solid' },
  { id: 'dashed', label: 'Dashed' },
]

// Trend-line family — the only overlays that expose the Extend control. A trend
// line is drawn as a "segment" and swaps type as it extends (ray / straight).
export const TREND_NAMES = ['segment', 'rayLine', 'straightLine']

export const EXTEND_LABEL: Record<string, string> = {
  none: 'Extend none',
  left: 'Extend left',
  right: 'Extend right',
  both: 'Extend both',
}

export function modeFromName(name: string, explicit?: string): string {
  if (explicit) return explicit
  if (name === 'straightLine') return 'both'
  if (name === 'rayLine') return 'right'
  return 'none'
}

export interface DragOffset {
  dx: number
  dy: number
}

/**
 * Shared drag handle for EVERY workbench floating/style toolbar (grip dots on the
 * far left). Holds no state of its own — the parent owns the { dx, dy } offset it
 * applies to the toolbar. Reuse this (never hand-roll a grip) so all toolbars
 * drag identically.
 */
export function Grip({
  drag,
  setDrag,
}: {
  drag: DragOffset
  setDrag: (d: DragOffset) => void
}) {
  const ref = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null)
  const onDown = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId)
    ref.current = { sx: e.clientX, sy: e.clientY, bx: drag.dx, by: drag.dy }
  }
  const onMove = (e: React.PointerEvent<HTMLSpanElement>) => {
    if (!ref.current) return
    setDrag({ dx: ref.current.bx + (e.clientX - ref.current.sx), dy: ref.current.by + (e.clientY - ref.current.sy) })
  }
  const onUp = (e: React.PointerEvent<HTMLSpanElement>) => {
    ref.current = null
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId)
    } catch {
      /* noop */
    }
  }
  return (
    <span
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      title="Drag to move"
      className="flex items-center px-1 text-muted-foreground/60 hover:text-muted-foreground cursor-grab active:cursor-grabbing touch-none"
    >
      <span className="grid grid-cols-2 gap-[3px]">
        {Array.from({ length: 6 }).map((_, i) => (
          <span key={i} className="h-[3px] w-[3px] rounded-full bg-current" />
        ))}
      </span>
    </span>
  )
}

export function ToolbarButton({
  children,
  active,
  title,
  onClick,
}: {
  children: ReactNode
  active?: boolean
  title?: string
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 min-w-7 items-center justify-center rounded-md px-1.5 transition-colors cursor-pointer',
        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

/**
 * Popover used inside the floating toolbars and settings dialogs. Radix-backed:
 * portalled and collision-clamped to the viewport, so it can never spill off a
 * phone screen or force a host dialog to overflow-visible. The parent still owns
 * the open state and the trigger's onClick toggle — anchor-based rather than
 * Trigger-based because ToolbarButton doesn't forward refs.
 */
export function BitsPopover({
  open,
  onOpenChange,
  anchor,
  align = 'left',
  children,
  onMouseDown,
  modal = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The trigger button — rendered inline as the positioning anchor. */
  anchor: ReactNode
  align?: 'left' | 'right'
  children: ReactNode
  /** NoteToolbar passes preventDefault so panel clicks never blur the note textarea. */
  onMouseDown?: (e: React.MouseEvent) => void
  modal?: boolean
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null)
  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={modal}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="relative">
          {anchor}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align={align === 'right' ? 'end' : 'start'}
        sideOffset={4}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          // The trigger's own onClick toggles open; without this guard the
          // outside-dismiss fires on pointerdown and the click reopens it.
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault()
        }}
        onMouseDown={onMouseDown}
        className="w-auto min-w-0 p-1.5 border-border shadow-popover"
      >
        {children}
      </PopoverContent>
    </Popover>
  )
}

export function CheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-caption text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors cursor-pointer whitespace-nowrap"
    >
      <span
        className={cn(
          'inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
          checked ? 'bg-primary border-primary text-white' : 'border-border',
        )}
      >
        {checked && <Check size={11} strokeWidth={3} />}
      </span>
      {label}
    </button>
  )
}
